import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { ArrowRight, CheckCircle2, Lock, Unlock, Share2, Users } from 'lucide-react';
import { useWaitlist } from './WaitlistContext';
const VERTICALS = ['Fashion & apparel', 'Furniture & homeware', 'Telco', 'Insurance', 'Other'];
const SIZES = ['< 100k accounts', '100k – 500k', '500k – 1M', '1M+'];
const ROLES = ['CFO / Finance', 'Head of Credit', 'Head of Collections', 'Operations', 'IT / Engineering', 'Other'];
const COLLECTION_METHODS = ['In-store at the till', 'Debit order', 'Bank EFT / transfer', 'Existing mobile app', 'Call centre', 'Other'];
export function Waitlist() {
  const {
    count,
    target,
    remaining,
    progress,
    isUnlocked,
    hasJoined,
    position,
    addSignup
  } = useWaitlist();
  const [form, setForm] = useState({
    name: '',
    workEmail: '',
    company: '',
    role: '',
    vertical: '',
    size: '',
    collectionMethod: ''
  });
  const [submitted, setSubmitted] = useState(hasJoined);
  const [myPosition, setMyPosition] = useState<number | null>(position);
  const [copied, setCopied] = useState(false);
  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (form.name && form.workEmail && form.company) {
      const pos = addSignup();
      setMyPosition(pos);
      setSubmitted(true);
    }
  };
  const update = (key: keyof typeof form) => (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => setForm(f => ({
    ...f,
    [key]: e.target.value
  }));
  const handleShare = () => {
    navigator.clipboard?.writeText('https://masube.co.za/?ref=retailer').catch(() => {});
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };
  return <section id="book-demo" className="py-24 bg-ink" data-id="element-549">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-550">
        <motion.div initial={{
        opacity: 0,
        y: 20
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true
      }} className="bg-ink-2 rounded-[2.5rem] p-8 md:p-12 lg:p-16 border border-white/10 relative overflow-hidden" data-id="element-551">
          
          <div className="absolute -top-32 -right-32 w-80 h-80 rounded-full bg-mint/10 blur-3xl pointer-events-none" data-id="element-552"></div>

          {/* Counted progress bar — the unlock mechanic */}
          <div className="relative z-10 mb-12" data-id="element-553">
            <div className="flex items-end justify-between mb-3 flex-wrap gap-2" data-id="element-554">
              <div className="flex items-center gap-3" data-id="element-555">
                <div className={`w-10 h-10 rounded-xl flex items-center justify-center ${isUnlocked ? 'bg-mint text-ink' : 'bg-mint-soft text-mint'}`} data-id="element-556">
                  
                  {isUnlocked ? <Unlock className="w-5 h-5" data-id="element-557" /> : <Lock className="w-5 h-5" data-id="element-558" />}
                </div>
                <div data-id="element-559">
                  <div className="text-xs font-medium uppercase tracking-[0.2em] text-mint" data-id="element-560">
                    Founding retailer waitlist
                  </div>
                  <div className="text-sm text-bone-dim" data-id="element-561">
                    {isUnlocked ? 'The live demo is unlocked for everyone 🎉' : `${remaining} more retailer${remaining === 1 ? '' : 's'} until the live demo unlocks`}
                  </div>
                </div>
              </div>
              <div className="text-right" data-id="element-562">
                <div className="display text-bone text-3xl leading-none" data-id="element-563">
                  {count}
                  <span className="text-muted text-xl" data-id="element-564"> / {target}</span>
                </div>
                <div className="text-xs text-muted flex items-center gap-1 justify-end mt-1" data-id="element-565">
                  <Users className="w-3 h-3" data-id="element-566" /> retailers joined
                </div>
              </div>
            </div>

            {/* Progress meter */}
            <div className="h-3 bg-ink rounded-full overflow-hidden border border-white/5" data-id="element-567">
              <motion.div className="h-full bg-mint rounded-full" initial={{
              width: 0
            }} whileInView={{
              width: `${progress * 100}%`
            }} viewport={{
              once: true
            }} transition={{
              duration: 1,
              ease: 'easeOut'
            }} data-id="element-568" />
              
            </div>
          </div>

          <div className="grid lg:grid-cols-2 gap-12 lg:gap-16 relative z-10" data-id="element-569">
            <div data-id="element-570">
              <div className="text-xs font-medium uppercase tracking-[0.2em] text-mint mb-4" data-id="element-571">
                {isUnlocked ? 'Book a demo' : 'Join the waitlist'}
              </div>
              <h2 className="display text-bone mb-6" style={{
              fontSize: 'clamp(2.25rem, 5vw, 4rem)'
            }} data-id="element-572">
                
                {isUnlocked ? <>
                    See it on{' '}
                    <span className="display-italic text-mint" data-id="element-573">your</span>{' '}
                    accounts in 30 minutes.
                  </> : <>
                    Be one of the{' '}
                    <span className="display-italic text-mint" data-id="element-574">first 100</span>{' '}
                    retailers.
                  </>}
              </h2>
              <p className="text-lg text-bone-dim mb-8 leading-relaxed" data-id="element-575">
                {isUnlocked ? "Show us your collections workflow and we'll walk through exactly how Masube would plug in — including expected lift, integration scope, and timeline." : 'When 100 retailers join, the interactive product demo unlocks for everyone on the list — and founding members get priority onboarding and pilot pricing.'}
              </p>

              <ul className="space-y-3" data-id="element-576">
                {(isUnlocked ? ['Live walkthrough on a test instance', 'Custom ROI projection for your portfolio', 'Integration scope & 14-day pilot plan', 'No commitment, no sales pressure'] : ['Unlock the full interactive demo at 100', 'Founding-member pilot pricing', 'Priority integration support', 'Move up the queue by referring retailers']).map(item => <li key={item} className="flex items-start gap-3 text-bone-dim" data-id="element-577">
                  
                    <CheckCircle2 className="w-5 h-5 text-mint flex-shrink-0 mt-0.5" data-id="element-578" />
                    <span data-id="element-579">{item}</span>
                  </li>)}
              </ul>
            </div>

            <div data-id="element-580">
              {submitted ? <motion.div initial={{
              opacity: 0,
              scale: 0.95
            }} animate={{
              opacity: 1,
              scale: 1
            }} className="bg-mint text-ink rounded-3xl p-10 h-full flex flex-col justify-center" data-id="element-581">
                
                  <CheckCircle2 className="w-12 h-12 mb-6" data-id="element-582" />
                  <h3 className="display text-3xl mb-3" data-id="element-583">
                    You're on the list
                    {form.name ? `, ${form.name.split(' ')[0]}` : ''}.
                  </h3>
                  {myPosition !== null && <p className="text-ink/80 text-lg mb-2" data-id="element-584">
                      You're retailer <strong data-id="element-585">#{myPosition}</strong> of {target}
                      .{' '}
                      {isUnlocked ? 'The demo is unlocked — scroll up to try it live.' : `${remaining} more to unlock the demo for everyone.`}
                    </p>}
                  <p className="text-ink/70 text-sm mb-6" data-id="element-586">
                    Want to skip the queue? Refer another retailer and you both
                    move up.
                  </p>
                  <button onClick={handleShare} className="inline-flex items-center justify-center gap-2 py-3 px-6 rounded-2xl bg-ink text-bone font-semibold text-sm hover:bg-ink-2 transition-colors self-start" data-id="element-587">
                  
                    <Share2 className="w-4 h-4" data-id="element-588" />
                    {copied ? 'Link copied!' : 'Copy referral link'}
                  </button>
                </motion.div> : <form onSubmit={handleSubmit} className="space-y-4" data-id="element-589">
                  <div data-id="element-590">
                    <label className="block text-xs font-medium text-muted uppercase tracking-wider mb-2" data-id="element-591">
                      Your name
                    </label>
                    <input type="text" required value={form.name} onChange={update('name')} placeholder="Thandi Mokoena" className="w-full px-5 py-4 bg-ink border border-white/10 rounded-2xl text-bone placeholder-muted focus:outline-none focus:border-mint focus:ring-4 focus:ring-mint/20 transition-all" data-id="element-592" />
                  
                  </div>
                  <div data-id="element-593">
                    <label className="block text-xs font-medium text-muted uppercase tracking-wider mb-2" data-id="element-594">
                      Work email
                    </label>
                    <input type="email" required value={form.workEmail} onChange={update('workEmail')} placeholder="thandi@yourcompany.co.za" className="w-full px-5 py-4 bg-ink border border-white/10 rounded-2xl text-bone placeholder-muted focus:outline-none focus:border-mint focus:ring-4 focus:ring-mint/20 transition-all" data-id="element-595" />
                  
                  </div>
                  <div className="grid grid-cols-2 gap-3" data-id="element-596">
                    <div data-id="element-597">
                      <label className="block text-xs font-medium text-muted uppercase tracking-wider mb-2" data-id="element-598">
                        Company
                      </label>
                      <input type="text" required value={form.company} onChange={update('company')} placeholder="e.g. Edgars" className="w-full px-5 py-4 bg-ink border border-white/10 rounded-2xl text-bone placeholder-muted focus:outline-none focus:border-mint focus:ring-4 focus:ring-mint/20 transition-all" data-id="element-599" />
                    
                    </div>
                    <div data-id="element-600">
                      <label className="block text-xs font-medium text-muted uppercase tracking-wider mb-2" data-id="element-601">
                        Role / title
                      </label>
                      <select value={form.role} onChange={update('role')} className="w-full px-5 py-4 bg-ink border border-white/10 rounded-2xl text-bone focus:outline-none focus:border-mint focus:ring-4 focus:ring-mint/20 transition-all" data-id="element-602">
                      
                        <option value="" data-id="element-603">Select…</option>
                        {ROLES.map(r => <option key={r} value={r} data-id="element-604">
                            {r}
                          </option>)}
                      </select>
                    </div>
                  </div>
                  <div data-id="element-605">
                    <label className="block text-xs font-medium text-muted uppercase tracking-wider mb-2" data-id="element-606">
                      Current collection method
                    </label>
                    <select value={form.collectionMethod} onChange={update('collectionMethod')} className="w-full px-5 py-4 bg-ink border border-white/10 rounded-2xl text-bone focus:outline-none focus:border-mint focus:ring-4 focus:ring-mint/20 transition-all" data-id="element-607">
                    
                      <option value="" data-id="element-608">How do customers pay today?</option>
                      {COLLECTION_METHODS.map(m => <option key={m} value={m} data-id="element-609">
                          {m}
                        </option>)}
                    </select>
                  </div>
                  <div className="grid grid-cols-2 gap-3" data-id="element-610">
                    <div data-id="element-611">
                      <label className="block text-xs font-medium text-muted uppercase tracking-wider mb-2" data-id="element-612">
                        Vertical
                      </label>
                      <select value={form.vertical} onChange={update('vertical')} className="w-full px-5 py-4 bg-ink border border-white/10 rounded-2xl text-bone focus:outline-none focus:border-mint focus:ring-4 focus:ring-mint/20 transition-all" data-id="element-613">
                      
                        <option value="" data-id="element-614">Select…</option>
                        {VERTICALS.map(v => <option key={v} value={v} data-id="element-615">
                            {v}
                          </option>)}
                      </select>
                    </div>
                    <div data-id="element-616">
                      <label className="block text-xs font-medium text-muted uppercase tracking-wider mb-2" data-id="element-617">
                        Account book
                      </label>
                      <select value={form.size} onChange={update('size')} className="w-full px-5 py-4 bg-ink border border-white/10 rounded-2xl text-bone focus:outline-none focus:border-mint focus:ring-4 focus:ring-mint/20 transition-all" data-id="element-618">
                      
                        <option value="" data-id="element-619">Select…</option>
                        {SIZES.map(s => <option key={s} value={s} data-id="element-620">
                            {s}
                          </option>)}
                      </select>
                    </div>
                  </div>

                  <button type="submit" className="w-full inline-flex items-center justify-center gap-2 py-5 px-8 rounded-2xl bg-mint text-ink font-semibold text-base hover:bg-mint-dim transition-colors shadow-[0_0_40px_-10px_rgba(43,227,140,0.6)] mt-2" data-id="element-621">
                  
                    {isUnlocked ? 'Book my demo' : 'Claim my spot'}
                    <ArrowRight className="w-4 h-4" data-id="element-622" />
                  </button>
                  <p className="text-xs text-muted text-center" data-id="element-623">
                    By joining you agree to our{' '}
                    <a href="/terms" className="text-bone-dim hover:text-mint underline" data-id="element-624">
                    
                      Terms
                    </a>{' '}
                    and{' '}
                    <a href="/privacy" className="text-bone-dim hover:text-mint underline" data-id="element-625">
                    
                      Privacy Policy
                    </a>
                    .
                  </p>
                </form>}
            </div>
          </div>
        </motion.div>
      </div>
    </section>;
}